import { useState } from 'react'
import { ListFilter } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuTrigger,
  DropdownMenuCheckboxItem,
} from '@/components/ui/dropdown-menu'
import { DatePicker } from '@/components/shared/date-picker'
import { useSystemSettings } from '@/stores/settings.store'
import { useOrganizationUsers } from '@/stores/organization.store'
import { TaskStatusEnum } from '@/gen/cortado/v1/settings_pb'
import { formatEnumName } from '@/lib/enum-utils'
import type { CustomDateRange } from '@/lib/date-filter-utils'

interface TasksKanbanFilterDropdownProps {
  selectedStatuses: TaskStatusEnum[]
  onStatusesChange: (statuses: TaskStatusEnum[]) => void
  selectedAssignees: string[]
  onAssigneesChange: (assignees: string[]) => void
  dueDateRange: CustomDateRange | null
  onDueDateRangeChange: (range: CustomDateRange | null) => void
}

export function TasksKanbanFilterDropdown({
  selectedStatuses,
  onStatusesChange,
  selectedAssignees,
  onAssigneesChange,
  dueDateRange,
  onDueDateRangeChange,
}: TasksKanbanFilterDropdownProps) {
  const [open, setOpen] = useState(false)
  const { settings } = useSystemSettings()
  const { users } = useOrganizationUsers()

  // Get task statuses from settings (excluding UNKNOWN)
  const taskStatuses = (settings?.taskStatuses || []).filter(
    (status) => status !== TaskStatusEnum.TASK_STATUS_UNKNOWN
  )

  // Toggle status selection
  const toggleStatus = (status: TaskStatusEnum) => {
    const newStatuses = selectedStatuses.includes(status)
      ? selectedStatuses.filter((s) => s !== status)
      : [...selectedStatuses, status]
    onStatusesChange(newStatuses)
  }

  // Toggle assignee selection
  const toggleAssignee = (userId: string) => {
    const newAssignees = selectedAssignees.includes(userId)
      ? selectedAssignees.filter((a) => a !== userId)
      : [...selectedAssignees, userId]
    onAssigneesChange(newAssignees)
  }

  // Count active filters
  const activeFilterCount =
    (selectedStatuses.length > 0 ? 1 : 0) +
    (selectedAssignees.length > 0 ? 1 : 0) +
    (dueDateRange?.from || dueDateRange?.to ? 1 : 0)

  const hasDueDateFilter = dueDateRange?.from || dueDateRange?.to

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 gap-1">
          <ListFilter className="h-4 w-4" />
          Filter
          {activeFilterCount > 0 && (
            <Badge variant="secondary" className="ml-1 rounded-sm px-1 font-normal">
              {activeFilterCount}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-[200px]">
        {/* Status Filter Submenu */}
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <span>Status</span>
            {selectedStatuses.length > 0 && (
              <Badge variant="secondary" size="count" className="ml-auto">
                {selectedStatuses.length}
              </Badge>
            )}
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent className="w-[180px]">
            {taskStatuses.map((status) => (
              <DropdownMenuCheckboxItem
                key={status}
                checked={selectedStatuses.includes(status)}
                onCheckedChange={() => toggleStatus(status)}
              >
                {formatEnumName(status, TaskStatusEnum)}
              </DropdownMenuCheckboxItem>
            ))}
            {selectedStatuses.length > 0 && (
              <>
                <DropdownMenuSeparator />
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start px-2 font-normal"
                  onClick={() => onStatusesChange([])}
                >
                  Clear filter
                </Button>
              </>
            )}
          </DropdownMenuSubContent>
        </DropdownMenuSub>

        <DropdownMenuSeparator />

        {/* Assignee Filter Submenu */}
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <span>Assignee</span>
            {selectedAssignees.length > 0 && (
              <Badge variant="secondary" size="count" className="ml-auto">
                {selectedAssignees.length}
              </Badge>
            )}
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent className="w-[200px]">
            {users.map((orgUser) => {
              const userId = orgUser.user?.id ?? ''
              const fullName = `${orgUser.user?.firstName || ''} ${orgUser.user?.lastName || ''}`.trim()
              return (
                <DropdownMenuCheckboxItem
                  key={userId}
                  checked={selectedAssignees.includes(userId)}
                  onCheckedChange={() => toggleAssignee(userId)}
                >
                  {fullName || 'Unknown User'}
                </DropdownMenuCheckboxItem>
              )
            })}
            {selectedAssignees.length > 0 && (
              <>
                <DropdownMenuSeparator />
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start px-2 font-normal"
                  onClick={() => onAssigneesChange([])}
                >
                  Clear filter
                </Button>
              </>
            )}
          </DropdownMenuSubContent>
        </DropdownMenuSub>

        <DropdownMenuSeparator />

        {/* Due Date Range Filter */}
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <span>Due Date</span>
            {hasDueDateFilter && (
              <Badge variant="secondary" size="count" className="ml-auto">
                1
              </Badge>
            )}
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent className="w-[280px] p-3">
            <div className="space-y-3">
              {/* Start Date */}
              <div className="space-y-1.5">
                <div className="text-sm font-medium">From</div>
                <DatePicker
                  value={dueDateRange?.from}
                  onChange={(date) => {
                    onDueDateRangeChange({
                      ...dueDateRange,
                      from: date,
                    })
                  }}
                  placeholder="Select start date"
                  startMonth={new Date(new Date().getFullYear() - 1, 0, 1)}
                  endMonth={new Date(new Date().getFullYear() + 2, 11, 31)}
                />
              </div>

              {/* End Date */}
              <div className="space-y-1.5">
                <div className="text-sm font-medium">To</div>
                <DatePicker
                  value={dueDateRange?.to}
                  onChange={(date) => {
                    onDueDateRangeChange({
                      ...dueDateRange,
                      to: date,
                    })
                  }}
                  placeholder="Select end date"
                  minDate={dueDateRange?.from}
                  startMonth={new Date(new Date().getFullYear() - 1, 0, 1)}
                  endMonth={new Date(new Date().getFullYear() + 2, 11, 31)}
                />
              </div>

              {hasDueDateFilter && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start px-2 font-normal"
                  onClick={() => onDueDateRangeChange(null)}
                >
                  Clear filter
                </Button>
              )}
            </div>
          </DropdownMenuSubContent>
        </DropdownMenuSub>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
